import { useMemo } from 'react';

import {
  useCompanyCodeDiagnostics,
  useCurrenciesDiagnostics,
  useLegalEntityDiagnostics,
  useObligationDiagnostics,
  useTaxBookingDiagnostics,
  useProvisionDiagnostics,
} from '../../shared/queries/diagnostics';
import { PillTypes } from './constants';

export const useDiagnosticsQueries = (periodName: string, activePill?: PillTypes | true) => {
  const {
    data: taxBookingDiagnostics,
    isLoading: isLoadingTaxBooking,
    isFetched: isFetchedTaxBooking,
    refetch: refetchTaxBookingDiagnostics,
  } = useTaxBookingDiagnostics({
    periodName,
    enabled: activePill === PillTypes.TAX_BOOKING_ENTITIES || activePill === true,
  });
  const {
    data: currenciesDiagnostics,
    isLoading: isLoadingCurrencies,
    isFetched: isFetchedCurrencies,
    refetch: refetchCurrenciesDiagnostics,
  } = useCurrenciesDiagnostics({
    periodName,
    enabled: activePill === PillTypes.CURRENCIES || activePill === true,
  });
  const {
    data: obligationDiagnostics,
    isLoading: isLoadingObligation,
    isFetched: isFetchedObligation,
    refetch: refetchObligationDiagnostics,
  } = useObligationDiagnostics({
    periodName,
    enabled: activePill === PillTypes.OBLIGATION || activePill === true,
  });
  const {
    data: companyCodeDiagnostics,
    isLoading: isLoadingCompanyCode,
    isFetched: isFetchedCompanyCode,
    refetch: refetchCompanyCodeDiagnostics,
  } = useCompanyCodeDiagnostics({
    periodName,
    enabled: activePill === PillTypes.COMPANY_CODE || activePill === true,
  });
  const {
    data: legalEntityDiagnostics,
    isLoading: isLoadingLegalEntity,
    isFetched: isFetchedLegalEntity,
    refetch: refetchLegalEntityDiagnostics,
  } = useLegalEntityDiagnostics({
    periodName,
    enabled: activePill === PillTypes.LEGAL_ENTITY || activePill === true,
  });
  const {
    data: provisionDiagnostics,
    isLoading: isLoadingProvision,
    isFetched: isFetchedProvision,
    refetch: refetchProvisionDiagnostics,
  } = useProvisionDiagnostics({
    periodName,
    enabled: activePill === PillTypes.PROVISION || activePill === true,
  });


  const isLoading = useMemo(() => ({
    [PillTypes.TAX_BOOKING_ENTITIES]: isLoadingTaxBooking,
    [PillTypes.CURRENCIES]: isLoadingCurrencies,
    [PillTypes.OBLIGATION]: isLoadingObligation,
    [PillTypes.COMPANY_CODE]: isLoadingCompanyCode,
    [PillTypes.LEGAL_ENTITY]: isLoadingLegalEntity,
    [PillTypes.PROVISION]: isLoadingProvision,
  }), [
    isLoadingTaxBooking,
    isLoadingCurrencies,
    isLoadingObligation,
    isLoadingCompanyCode,
    isLoadingLegalEntity,
    isLoadingProvision,
  ]);

  const data = useMemo(() => ({
    [PillTypes.TAX_BOOKING_ENTITIES]: taxBookingDiagnostics,
    [PillTypes.CURRENCIES]: currenciesDiagnostics,
    [PillTypes.OBLIGATION]: obligationDiagnostics,
    [PillTypes.COMPANY_CODE]: companyCodeDiagnostics,
    [PillTypes.LEGAL_ENTITY]: legalEntityDiagnostics,
    [PillTypes.PROVISION]: provisionDiagnostics,
  }), [
    taxBookingDiagnostics,
    currenciesDiagnostics,
    obligationDiagnostics,
    companyCodeDiagnostics,
    legalEntityDiagnostics,
    provisionDiagnostics,
  ]);

  const isFetched = useMemo(() => ({
    [PillTypes.TAX_BOOKING_ENTITIES]: isFetchedTaxBooking,
    [PillTypes.CURRENCIES]: isFetchedCurrencies,
    [PillTypes.OBLIGATION]: isFetchedObligation,
    [PillTypes.COMPANY_CODE]: isFetchedCompanyCode,
    [PillTypes.LEGAL_ENTITY]: isFetchedLegalEntity,
    [PillTypes.PROVISION]: isFetchedProvision,
  }), [
    isFetchedTaxBooking,
    isFetchedCurrencies,
    isFetchedObligation,
    isFetchedCompanyCode,
    isFetchedLegalEntity,
    isFetchedProvision,
  ]);

  const refetch = useMemo(() => ({
    [PillTypes.TAX_BOOKING_ENTITIES]: refetchTaxBookingDiagnostics,
    [PillTypes.CURRENCIES]: refetchCurrenciesDiagnostics,
    [PillTypes.OBLIGATION]: refetchObligationDiagnostics,
    [PillTypes.COMPANY_CODE]: refetchCompanyCodeDiagnostics,
    [PillTypes.LEGAL_ENTITY]: refetchLegalEntityDiagnostics,
    [PillTypes.PROVISION]: refetchProvisionDiagnostics,
  }), [
    refetchTaxBookingDiagnostics,
    refetchCurrenciesDiagnostics,
    refetchObligationDiagnostics,
    refetchCompanyCodeDiagnostics,
    refetchLegalEntityDiagnostics,
    refetchProvisionDiagnostics,
  ]);

  return useMemo(() => ({
    isLoading,
    data,
    refetch,
    isFetched,
  }), [
    isLoading,
    data,
    refetch,
    isFetched,
  ]);
};
